import React, { useState, useEffect } from 'react';
import {
  Risk,
  RiskMatrix,
  RiskStatus,
  RiskTolerability,
  RiskDashboardStats,
} from './types';
import { riskService } from './riskService';
import { RisksTable } from './components/RisksTable';
import { RiskMatrixHeatmap } from './components/RiskMatrixHeatmap';
import { CreateRiskModal } from './components/CreateRiskModal';
import { RiskDetailModal } from './components/RiskDetailModal';
import { BowtieTab } from './bowtie/BowtieTab';
import { CentralRiskOverviewBoardTab } from './components/CentralRiskOverviewBoardTab';

type SubmenuView = 'register' | 'bowtie' | 'matrix' | 'mitigation' | 'accepted' | 'closed' | 'overview_board';

interface RisksPageProps {
  initialRiskId?: string;
  initialView?: SubmenuView;
}

export const RisksPage: React.FC<RisksPageProps> = ({
  initialRiskId,
  initialView = 'register',
}) => {
  const [currentView, setCurrentView] = useState<SubmenuView>(() => {
    const viewParam = new URLSearchParams(window.location.search).get('view') as SubmenuView | null;
    if (viewParam && ['register', 'bowtie', 'matrix', 'mitigation', 'accepted', 'closed', 'overview_board'].includes(viewParam)) {
      return viewParam;
    }
    return initialRiskId ? 'bowtie' : initialView;
  });
  const [activeBowtieRiskId, setActiveBowtieRiskId] = useState<string | null>(initialRiskId || null);

  useEffect(() => {
    if (initialRiskId) {
      setActiveBowtieRiskId(initialRiskId);
      setCurrentView('bowtie');
    }
  }, [initialRiskId]);
  const [risks, setRisks] = useState<Risk[]>([]);
  const [stats, setStats] = useState<RiskDashboardStats | null>(null);
  const [matrix, setMatrix] = useState<RiskMatrix | null>(null);
  const [heatmapDistribution, setHeatmapDistribution] = useState<Record<string, number>>({});
  const [useResidual, setUseResidual] = useState<boolean>(false);
  const [selectedCell, setSelectedCell] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTolerability, setSelectedTolerability] = useState<string>('');
  const [selectedRisk, setSelectedRisk] = useState<Risk | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const fetchStats = async () => {
    try {
      const data = await riskService.getDashboardStats();
      setStats(data);
    } catch (err: any) {
      console.error('Failed to load risk dashboard stats:', err);
    }
  };

  const fetchHeatmap = async (residual: boolean = useResidual) => {
    try {
      const data = await riskService.getHeatmapData('ORG-XYZ', residual);
      setMatrix(data?.matrix || null);
      setHeatmapDistribution(data?.distribution || {});
    } catch (err: any) {
      console.error('Failed to load risk heatmap data:', err);
    }
  };

  const fetchRisks = async (overrideSearch?: string) => {
    try {
      setLoading(true);
      let statusFilter: RiskStatus[] | undefined = undefined;

      if (currentView === 'mitigation') {
        statusFilter = ['MITIGATION_PLANNED', 'MITIGATION_IN_PROGRESS'];
      } else if (currentView === 'accepted') {
        statusFilter = ['RESIDUAL_ACCEPTED'];
      } else if (currentView === 'closed') {
        statusFilter = ['CLOSED'];
      }

      const term = overrideSearch !== undefined ? overrideSearch : searchTerm;
      const res = await riskService.getRisks({
        status: statusFilter,
        tolerability: selectedTolerability ? (selectedTolerability as RiskTolerability) : undefined,
        searchTerm: term.trim() || undefined,
      });

      setRisks(res.items || []);
    } catch (err: any) {
      console.error('Failed to load risks:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    fetchHeatmap(useResidual);
  }, [useResidual]);

  useEffect(() => {
    fetchRisks();
  }, [currentView, selectedTolerability]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    fetchRisks();
  };

  return (
    <div style={{ maxWidth: '1440px', margin: '0 auto', padding: '0 24px', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      {/* Top Header */}
      <div className="page-header-banner">
        <div className="badge">SAFETY MANAGEMENT</div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <h1>Safety Risk Management</h1>
            <p>ICAO Doc 9859 5x5 assessment, ALARP demonstration, barrier effectiveness, and formal acceptance.</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn-primary"
            style={{ width: 'auto', padding: '10px 18px' }}
          >
            + Register Risk
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      {stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-label">Total Risks</div>
            <div className="stat-value">{stats?.totalRisks || 0}</div>
            <div className="stat-sub">Assessed hazards</div>
          </div>

          <div className="stat-card">
            <div className="stat-label">Intolerable (High)</div>
            <div className="stat-value" style={{ color: '#dc2626' }}>{stats?.intolerableCount || 0}</div>
            <div className="stat-sub">Red zone risks</div>
          </div>

          <div className="stat-card">
            <div className="stat-label">Tolerable (ALARP)</div>
            <div className="stat-value" style={{ color: '#d97706' }}>{stats?.tolerableCount || 0}</div>
            <div className="stat-sub">Yellow zone risks</div>
          </div>

          <div className="stat-card">
            <div className="stat-label">Acceptable</div>
            <div className="stat-value" style={{ color: '#16a34a' }}>{stats?.acceptableCount || 0}</div>
            <div className="stat-sub">Green zone risks</div>
          </div>

          <div className="stat-card">
            <div className="stat-label">Active Mitigations</div>
            <div className="stat-value" style={{ color: '#7c3aed' }}>{stats?.activeMitigationCount || 0}</div>
            <div className="stat-sub">Risk treatment</div>
          </div>

          <div className="stat-card">
            <div className="stat-label">Overdue Reviews</div>
            <div className="stat-value" style={{ color: stats?.overdueReviewCount ? '#dc2626' : '#0f172a' }}>
              {stats?.overdueReviewCount || 0}
            </div>
            <div className="stat-sub">Review threshold</div>
          </div>
        </div>
      )}

      {/* Submenu Navigation */}
      <div className="nav-tabs">
        {[
          { id: 'register', label: 'Risk Register (Table)' },
          { id: 'bowtie', label: '🎀 Bowtie Diagram (Visual Barriers)' },
          { id: 'matrix', label: 'ICAO 5x5 Matrix & Heatmap' },
          { id: 'mitigation', label: 'Active Mitigations' },
          { id: 'accepted', label: 'Residual Accepted (ALARP)' },
          { id: 'closed', label: 'Closed / Archived' },
          { id: 'overview_board', label: '📊 Central Overview Board (SLA & EASA GM3)' },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => {
              const nextView = tab.id as SubmenuView;
              setCurrentView(nextView);
              const params = new URLSearchParams(window.location.search);
              params.set('view', nextView);
              window.history.pushState({}, '', `${window.location.pathname}?${params.toString()}`);
            }}
            className={`tab-btn ${currentView === tab.id ? 'active' : ''}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

        {/* Search & Filter Bar (Only shown on table views) */}
        {currentView !== 'matrix' && currentView !== 'bowtie' && (
          <form onSubmit={handleSearchSubmit} style={{ display: 'flex', gap: '8px', alignItems: 'center', paddingBottom: '6px' }}>
            <input
              type="text"
              placeholder="Search risk number or title..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                padding: '6px 12px',
                fontSize: '12px',
                borderRadius: '6px',
                border: '1px solid #cbd5e1',
                width: '220px',
              }}
            />

            <select
              value={selectedTolerability}
              onChange={(e) => setSelectedTolerability(e.target.value)}
              style={{
                padding: '6px 10px',
                fontSize: '12px',
                borderRadius: '6px',
                border: '1px solid #cbd5e1',
                background: '#ffffff',
              }}
            >
              <option value="">All Tolerabilities</option>
              <option value="INTOLERABLE">Intolerable (Red)</option>
              <option value="TOLERABLE">Tolerable (Amber)</option>
              <option value="ACCEPTABLE">Acceptable (Green)</option>
            </select>

            <button
              type="submit"
              style={{
                padding: '6px 12px',
                fontSize: '12px',
                fontWeight: 600,
                borderRadius: '6px',
                border: 'none',
                background: '#0284c7',
                color: '#ffffff',
                cursor: 'pointer',
              }}
            >
              Filter
            </button>
          </form>
        )}

      {/* Main View Body */}
      {currentView === 'matrix' ? (
        <RiskMatrixHeatmap
          matrix={matrix}
          distribution={heatmapDistribution}
          selectedCell={selectedCell}
          useResidual={useResidual}
          onToggleResidual={setUseResidual}
          onCellClick={(cellIndex) => {
            setSelectedCell(cellIndex);
            setCurrentView('register');
            setSearchTerm(cellIndex);
            fetchRisks(cellIndex);
          }}
        />
      ) : currentView === 'overview_board' ? (
        <CentralRiskOverviewBoardTab />
      ) : currentView === 'bowtie' ? (
        <BowtieTab risks={risks} initialRiskId={activeBowtieRiskId || undefined} />
      ) : (
        <RisksTable
          risks={risks}
          loading={loading}
          onSelect={(r) => setSelectedRisk(r)}
          onViewBowtie={(r) => {
            setActiveBowtieRiskId(r.id);
            setCurrentView('bowtie');
          }}
          onRefresh={() => {
            fetchRisks();
            fetchStats();
            fetchHeatmap();
          }}
        />
      )}

      {/* Modals */}
      {showCreateModal && (
        <CreateRiskModal
          onClose={() => setShowCreateModal(false)}
          onCreated={() => {
            setShowCreateModal(false);
            fetchRisks();
            fetchStats();
            fetchHeatmap();
          }}
        />
      )}

      {selectedRisk && (
        <RiskDetailModal
          riskId={selectedRisk.id}
          onClose={() => setSelectedRisk(null)}
          onUpdated={() => {
            fetchRisks();
            fetchStats();
            fetchHeatmap();
          }}
        />
      )}
    </div>
  );
};
